import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { allContent, categories } from '@/mocks/siteData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = allContent.find((item) => item.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto flex items-center justify-center bg-background-100 rounded-full mb-4">
              <i className="ri-error-warning-line text-3xl text-foreground-400"></i>
            </div>
            <h2 className="text-xl font-semibold text-foreground-600 mb-2">内容不存在</h2>
            <p className="text-sm text-foreground-400 mb-6">该内容可能已被移除或链接失效</p>
            <Link to="/" className="inline-flex px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedContent = allContent
    .filter((item) => item.category === article.category && item.id !== article.id)
    .slice(0, 4);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: `${article.title} - 在线观看高清视频`,
    description: article.description,
    image: article.image,
    datePublished: `${article.year}-01-01`,
    author: {
      '@type': 'Person',
      name: article.director,
    },
    publisher: {
      '@type': 'Organization',
      name: '极速高清',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://movihubs.github.io/article/${article.id}`,
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <ScrollToTop />
      <Navbar />
      <main className="pt-16 min-h-screen">
        <div className="bg-background-100 border-b border-background-200/70">
          <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: article.categoryName, href: `/category/${article.category}` },
                { label: article.title },
              ]}
            />
          </div>
        </div>

        <article className="max-w-5xl mx-auto px-4 md:px-6 py-10">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full">
                {article.categoryName}
              </span>
              {article.isHot && (
                <span className="px-3 py-1 bg-accent-100 text-accent-700 text-xs font-medium rounded-full flex items-center gap-1">
                  <i className="ri-fire-line"></i> 热门
                </span>
              )}
              {article.isNew && (
                <span className="px-3 py-1 bg-secondary-100 text-secondary-700 text-xs font-medium rounded-full">
                  NEW
                </span>
              )}
            </div>
            <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
              <span className="flex items-center gap-1">
                <i className="ri-star-fill text-secondary-400"></i>
                <strong className="text-foreground-800">{article.rating}</strong> 分
              </span>
              <span>{article.year}</span>
              <span>{article.duration}</span>
              <span>{article.views.toLocaleString()}次观看</span>
              <span>导演：{article.director}</span>
            </div>
          </div>

          <div className="aspect-video rounded-xl overflow-hidden mb-8 bg-background-200">
            <img
              src={article.image}
              alt={`在线观看${article.title} - 极速高清`}
              title={`在线观看${article.title}`}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="flex items-center gap-3 mb-8">
            <Link
              to={`/category/${article.category}`}
              className="inline-flex items-center gap-2 px-8 py-3 bg-primary-500 text-background-50 rounded-full text-base font-semibold hover:bg-primary-600 transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-play-circle-fill text-xl"></i>
              开始在线观看
            </Link>
            <button className="w-11 h-11 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
              <i className="ri-heart-line text-lg"></i>
            </button>
            <button className="w-11 h-11 flex items-center justify-center rounded-full border border-background-300 text-foreground-500 hover:text-primary-500 hover:border-primary-300 transition-colors cursor-pointer">
              <i className="ri-share-forward-line text-lg"></i>
            </button>
          </div>

          <div className="prose max-w-none mb-8">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">内容简介</h2>
            <p className="text-foreground-700 leading-relaxed text-base">
              {article.description}
            </p>
          </div>

          <div className="mb-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-4">影片信息</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { label: '导演', value: article.director },
                { label: '年份', value: article.year },
                { label: '时长', value: article.duration },
                { label: '评分', value: `${article.rating}分` },
                { label: '分类', value: article.categoryName },
                { label: '观看次数', value: `${article.views.toLocaleString()}次` },
              ].map((info) => (
                <div key={info.label} className="p-4 rounded-lg bg-background-100 border border-background-200/70">
                  <span className="text-xs text-foreground-400">{info.label}</span>
                  <p className="text-sm font-semibold text-foreground-800 mt-1">{info.value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mb-10">
            <h2 className="font-heading text-xl font-bold text-foreground-950 mb-3">标签</h2>
            <div className="flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-3 py-1.5 bg-background-100 border border-background-200/70 text-foreground-600 text-sm rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          {relatedContent.length > 0 && (
            <section>
              <h2 className="font-heading text-xl font-bold text-foreground-950 mb-6">相关推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {relatedContent.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all cursor-pointer"
                  >
                    <div className="relative aspect-video overflow-hidden">
                      <img
                        src={item.image}
                        alt={`在线观看${item.title}`}
                        title={`在线观看${item.title}`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-2 left-2">
                        <span className="px-2 py-0.5 bg-black/60 backdrop-blur-sm text-white text-xs rounded-md">
                          {item.rating}
                        </span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-1">
                        {item.title}
                      </h3>
                      <span className="text-xs text-foreground-400 mt-1">{item.year}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>
      <Footer />
    </>
  );
}